import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { LocalStorageKeys } from '@src/enums/localStorageKeys.enum';
import { useLocalStorage } from '../useLocalStorage';
import { UseChatMessageParams, UseChatMessageValue } from './interfaces/useChatMessage.interface';
import { UpdateMessageInLocalStorageParams } from './interfaces/updateMessageInLocalStorage.interface';

export const useChatMessage = ({ chatId }: UseChatMessageParams): UseChatMessageValue => {
  const [chatMessage, setChatMessage] = useState<string>('');

  const [localStorageMessages, setLocalStorageMessages] = useLocalStorage<Record<string, string>>(
    LocalStorageKeys.SAVED_CHAT_MESSAGES,
    {},
  );

  const updateMessageInLocalStorage = useCallback(
    debounce(({ message, chatId }: UpdateMessageInLocalStorageParams): void => {
      setLocalStorageMessages({
        ...localStorageMessages,
        [chatId]: message,
      });
    }, 500),
    [],
  );

  const handleSetChatMessage = (message: string): void => {
    setChatMessage(message);

    if (!chatId) {
      return;
    }

    updateMessageInLocalStorage({ chatId, message });
  };

  const clearChatMessage = () => {
    setChatMessage('');

    if (!chatId) {
      return;
    }

    const updatedMessages = { ...localStorageMessages };

    delete updatedMessages[chatId];

    setLocalStorageMessages(updatedMessages);
  };

  useEffect(() => {
    if (!chatId) {
      return;
    }

    setChatMessage(localStorageMessages?.[chatId] || '');
  }, [chatId]);

  return {
    chatMessage: chatMessage,
    clearChatMessage: clearChatMessage,
    setChatMessage: handleSetChatMessage,
  };
};
